import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

export interface EventDto {
  id: number;
  bookingId: number;
  organizationId: number;
  name: string;
  description?: string;
  scheduledDateTime: string;
  numberOfRounds: number;
  status: string;
  bookingStatus: string;
  dateCreated: string;
  isEnabled?: boolean;
  isDeleted?: boolean;
  eventTeams?: EventTeamDto[];
  teams?: EventTeamDto[];
}

export interface EventTeamDto {
  id: number;
  teamId: number;
  team?: EventTeamSummaryDto;
}

export interface EventTeamSummaryDto {
  id: number;
  name: string;
}

export interface EventCreateDto {
  bookingId: number;
  organizationId?: number;
  name: string;
  description?: string;
  scheduledDateTime?: string;
}

export interface EventUpdateDto {
  bookingId?: number;
  organizationId?: number;
  questionId?: number;
  dateCreated?: string;
  name: string;
  description?: string;
  scheduledDateTime?: string;
  status?: string;
  bookingStatus?: string;
}

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api/events';

  /**
   * Get all events
   */
  getAll(): Observable<EventDto[]> {
    return this.http.get<EventDto[]>(this.apiUrl);
  }

  /**
   * Get event by ID
   */
  getById(id: number): Observable<EventDto> {
    return this.http.get<EventDto>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create new event
   */
  create(data: EventCreateDto): Observable<EventDto> {
    return this.http.post<EventDto>(this.apiUrl, data);
  }

  /**
   * Update event
   */
  update(id: number, data: EventUpdateDto): Observable<EventDto> {
    return this.http.put<EventDto>(`${this.apiUrl}/${id}`, data);
  }

  /**
   * Delete event
   */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  setEnabled(id: number, isEnabled: boolean): Observable<EventDto> {
    return this.http.put<EventDto>(`${this.apiUrl}/${id}/enabled`, { isEnabled });
  }

  restore(id: number): Observable<EventDto> {
    return this.http.put<EventDto>(`${this.apiUrl}/${id}/restore`, {});
  }

  hardDelete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}/hard`);
  }

  /**
   * Assign team to event
   */
  assignTeam(eventId: number, teamId: number): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/${eventId}/teams`,
      { teamId }
    );
  }

  /**
   * Remove team from event
   */
  removeTeam(eventId: number, teamId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${eventId}/teams/${teamId}`
    );
  }
}
